import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Archive, Pencil, Plus } from "lucide-react";
import { useAppDispatch } from "../../app/hooks";
import { Programme, Topic } from "../../features/training/models/Programme";
import { archiveTopic } from "../../features/training/slice-extensions/training-slice-extension";
import TopicFormDialog from "./TopicFormDialog";

interface ProgrammeTopicsTableProps {
  programme: Programme;
  canManage: boolean;
}

/**
 * A programme's curriculum. Archived topics are hidden from the live view and
 * shown, marked, behind "Show archived"; they are never deleted.
 */
const ProgrammeTopicsTable: React.FC<ProgrammeTopicsTableProps> = ({ programme, canManage }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Topic | "new" | null>(null);

  const rows = programme.topics.filter((topic) => showArchived || !topic.isArchived);
  const nextSortOrder = programme.topics.reduce((max, topic) => Math.max(max, topic.sortOrder), 0) + 1;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-grey-10 dark:text-grey-90">{t("training.topics.title")}</h3>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-grey-30 dark:text-grey-80">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            {t("training.topics.showArchived")}
          </label>
          {canManage && (
            <button type="button" className="btn btn-primary" onClick={() => setEditing("new")}>
              <Plus size={18} strokeWidth={2} />
              <span>{t("training.topics.add")}</span>
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-grey-90 dark:border-primary-20">
        <table className="w-full text-sm">
          <thead className="bg-grey-95 dark:bg-primary-20 text-grey-30 dark:text-grey-90">
            <tr>
              <th className="px-4 py-2 text-left">{t("training.topics.code")}</th>
              <th className="px-4 py-2 text-left">{t("training.topics.topicTitle")}</th>
              <th className="px-4 py-2 text-left">{t("training.topics.kindLabel")}</th>
              <th className="px-4 py-2 text-right">{t("training.topics.unitMinutes")}</th>
              <th className="px-4 py-2 text-right">{t("training.topics.requiredCount")}</th>
              {canManage && <th className="px-4 py-2" />}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-primary-10 text-grey-20 dark:text-white">
            {rows.map((topic) => (
              <tr key={topic.id} className="border-t border-grey-90 dark:border-primary-20">
                <td className="px-4 py-2 font-medium">{topic.code}</td>
                <td className={`px-4 py-2 ${topic.isArchived ? "text-grey-50 line-through dark:text-grey-60" : ""}`}>
                  {topic.title}
                  {topic.isArchived && (
                    <span className="ml-2 text-xs text-grey-50 dark:text-grey-70">
                      ({t("training.topics.archivedMarker")})
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">{t(`training.topics.kind.${topic.kind}`)}</td>
                <td className="px-4 py-2 text-right">{topic.unitMinutes}</td>
                <td className="px-4 py-2 text-right">{topic.requiredCount}</td>
                {canManage && (
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    {!topic.isArchived && (
                      <>
                        <button type="button" className="btn btn-text" onClick={() => setEditing(topic)}
                          aria-label={`${t("training.topics.edit")} ${topic.code}`}>
                          <Pencil size={14} />
                        </button>
                        <button type="button" className="btn btn-text"
                          aria-label={`${t("training.topics.archive")} ${topic.code}`}
                          onClick={() => dispatch(archiveTopic({ programmeId: programme.id, topicId: topic.id }))}>
                          <Archive size={14} />
                        </button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td className="px-4 py-3 text-grey-40 dark:text-grey-80" colSpan={canManage ? 6 : 5}>
                  {canManage
                    ? t("training.topics.emptyAddFirst", { action: t("training.topics.add") })
                    : t("training.topics.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <TopicFormDialog
          programmeId={programme.id}
          topic={editing === "new" ? undefined : editing}
          nextSortOrder={nextSortOrder}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

export default ProgrammeTopicsTable;
